export async function checkAuth() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000); // 15s timeout
  try {
    const res = await fetch('/api/auth/status', {
      credentials: 'include',
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) {
      return { authenticated: false, email: null };
    }
    const data = await res.json();
    return data;
  } catch (e) {
    clearTimeout(timeout);
    console.error('[API] checkAuth failed:', e);
    return { authenticated: false, email: null };
  }
}

async function _errFromRes(res) {
  const text = await res.text().catch(() => '');
  try {
    const j = JSON.parse(text);
    if (j.detail != null) {
      return typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
    }
  } catch {
    /* plain text body */
  }
  return text || res.statusText || 'Request failed';
}

export async function fetchAccountSummary() {
  const res = await fetch('/api/account', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load account');
  return res.json();
}

export async function selectBillingPlan(plan) {
  const res = await fetch('/api/billing/select-plan', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) throw new Error(await _errFromRes(res));
  return res.json();
}

export async function setupTwoFactor() {
  const res = await fetch('/api/2fa/setup', { method: 'POST', credentials: 'include' });
  if (!res.ok) throw new Error('2FA setup failed');
  return res.json();
}

export async function enableTwoFactor(code) {
  const res = await fetch('/api/2fa/enable', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(await _errFromRes(res));
  return res.json();
}

export async function disableTwoFactor(code) {
  const res = await fetch('/api/2fa/disable', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(await _errFromRes(res));
  return res.json();
}

export async function verifyTwoFactorSession(code) {
  const res = await fetch('/api/2fa/verify-session', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(await _errFromRes(res));
  return res.json();
}

export async function getLoginUrl() {
  const res = await fetch('/api/auth/login', { credentials: 'include' });
  const data = await res.json();
  console.log('[API] getLoginUrl:', data.auth_url);
  return data.auth_url;
}

export async function logout() {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export async function fetchEmails(count = 50, folder = 'inbox') {
  const res = await fetch(`/api/emails?count=${count}&folder=${folder}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch emails');
  const data = await res.json();
  return data.emails;
}

export async function fetchSnoozeLabelId() {
  const res = await fetch('/api/emails/snooze-label', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch snooze label');
  const data = await res.json();
  return data.snooze_label_id;
}

/** @returns {{ success: boolean, labels: string[] }} */
export async function starEmail(messageId, starred = true) {
  const res = await fetch('/api/emails/star', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_id: messageId, starred })
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || 'Failed to star email');
  }
  const data = await res.json();
  return { success: !!data.success, labels: data.labels || [] };
}

/** @returns {{ success: boolean, labels: string[] }} */
export async function snoozeEmail(messageId, snoozed = true) {
  const res = await fetch('/api/emails/snooze', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_id: messageId, snoozed })
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || 'Failed to snooze email');
  }
  const data = await res.json();
  return { success: !!data.success, labels: data.labels || [] };
}

export async function fetchEmailDetail(messageId) {
  const res = await fetch(`/api/emails/detail/${encodeURIComponent(messageId)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load email');
  const data = await res.json();
  return data.email;
}

export async function downloadAttachmentBlob(messageId, attachment, { forPreview = false } = {}) {
  const mime = (attachment.mime_type || 'application/octet-stream').trim() || 'application/octet-stream';
  const q = new URLSearchParams({
    filename: attachment.filename || 'file',
    mime_type: mime,
  });
  if (forPreview) q.set('inline', '1');
  const url = `/api/emails/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachment.id)}?${q}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Download failed');
  const headerType = res.headers.get('Content-Type')?.split(';')[0]?.trim();
  const blob = await res.blob();
  const effectiveType =
    headerType && headerType !== 'application/octet-stream' ? headerType : blob.type || mime;
  if (effectiveType && effectiveType !== blob.type) {
    const buf = await blob.arrayBuffer();
    return new Blob([buf], { type: effectiveType });
  }
  return blob;
}

export async function sendEmail({ to, subject, body, cc = [] }) {
  const res = await fetch('/api/emails/send', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, body, cc }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || 'Send failed');
  }
  return res.json();
}

export async function saveDraft({ to, subject, body, cc = [] }) {
  const res = await fetch('/api/drafts/save', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, body, cc }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || 'Save draft failed');
  }
  return res.json();
}

export async function updateDraft({ messageId, to, subject, body, cc = [] }) {
  const res = await fetch('/api/drafts/update', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message_id: messageId, to, subject, body, cc }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || 'Update draft failed');
  }
  return res.json();
}

export async function composeWithAI(payload) {
  const res = await fetch('/api/compose/ai', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || 'AI compose failed');
  }
  return res.json();
}

/**
 * Start processing emails via SSE using fetch() instead of EventSource.
 * EventSource does NOT support credentials (cookies), which causes 401 errors.
 * This fetch-based approach sends the auth cookie properly.
 */
export function startProcessing(emailIds, { onProgress, onComplete, onError }) {
  const ids = emailIds.join(',');
  const controller = new AbortController();

  (async () => {
    try {
      console.log('[API] startProcessing for IDs:', emailIds);
      const res = await fetch(`/api/process`, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: { 
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: emailIds })
      });

      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        console.error('[API] Processing failed with status:', res.status, text);
        let msg = `Server error ${res.status}: ${text}`;
        try {
          const j = JSON.parse(text);
          const d = j.detail;
          if (res.status === 402 && typeof d === 'string') {
            const inner = d.startsWith('{') ? JSON.parse(d) : null;
            if (inner?.error === 'quota_exceeded') {
              msg = `Monthly analysis quota reached (${inner.remaining ?? 0} left). Upgrade your plan in Settings → Billing.`;
            }
          } else if (typeof d === 'string' && d.length < 400) {
            msg = d;
          }
        } catch (_) {
          /* use msg */
        }
        if (onError) onError(msg);
        return;
      }

      console.log('[API] SSE stream established');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[API] SSE data received:', data);
              if (data.type === 'progress' && onProgress) {
                onProgress(data);
              } else if (data.type === 'complete' && onComplete) {
                console.log('[API] SSE complete');
                onComplete();
              } else if (data.type === 'error' && onError) {
                console.error('[API] SSE error data:', data.message);
                onError(data.message);
              }
            } catch (e) {
              console.error('SSE parse error:', e);
            }
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[API] Processing stream error:', e);
        if (onError) onError('Connection lost');
      } else {
        console.log('[API] Processing stream aborted');
      }
    }
  })();

  // Return cancel function
  return () => {
    controller.abort();
  };
}
